/**
 * CSP-safe dynamic styles: inject style objects/strings into a nonced <style> block
 * and return a class name. Use :class="styleToClass(...)" instead of :style.
 */

const DYNAMIC_STYLE_ID = 'dynamic-inline-styles'
const RUNTIME_STYLE_ID = 'dynamic-runtime-styles'
const CLASS_PREFIX = 'dyn-'

let styleEl = null
let runtimeStyleEl = null
const cache = new Map()
const runtimeRules = new Map() // selector -> cssText (for setDynamicRule)
let idCounter = 0

/** Shared: get CSP nonce from meta tag (used by styleToClass, csp-theme-styles, etc.) */
export function getCspNonce() {
  if (typeof document === 'undefined') return null
  const meta = document.querySelector('meta[name="csp-nonce"]')
  return meta ? meta.getAttribute('content') : null
}

export function getOrCreateDynamicStyleElement() {
  if (styleEl && styleEl.parentNode) return styleEl
  if (typeof document === 'undefined') return null
  styleEl = document.getElementById(DYNAMIC_STYLE_ID)
  if (styleEl) return styleEl
  styleEl = document.createElement('style')
  styleEl.id = DYNAMIC_STYLE_ID
  const nonce = getCspNonce()
  if (nonce) styleEl.setAttribute('nonce', nonce)
  document.head.appendChild(styleEl)
  return styleEl
}

/**
 * Get or create the runtime <style> element used for selector-based rules
 * (e.g. :root custom properties, iframe heights). CSP-safe: uses nonce.
 */
export function getOrCreateRuntimeStyleElement() {
  if (runtimeStyleEl && runtimeStyleEl.parentNode) return runtimeStyleEl
  if (typeof document === 'undefined') return null
  runtimeStyleEl = document.getElementById(RUNTIME_STYLE_ID)
  if (runtimeStyleEl) return runtimeStyleEl
  runtimeStyleEl = document.createElement('style')
  runtimeStyleEl.id = RUNTIME_STYLE_ID
  const nonce = getCspNonce()
  if (nonce) runtimeStyleEl.setAttribute('nonce', nonce)
  document.head.appendChild(runtimeStyleEl)
  return runtimeStyleEl
}

/**
 * Set or update a single CSS rule by selector in the nonced runtime style element.
 * Use for dynamic values (e.g. :root { --app-height }, iframe heights) to avoid inline styles.
 * @param {string} selector - CSS selector (e.g. ':root', '[data-freedompay-iframe="CardPay"] iframe')
 * @param {Object|string} css - Object of camelCase props or string like 'height: 400px'
 */
export function setDynamicRule(selector, css) {
  if (typeof document === 'undefined') return
  const cssText = typeof css === 'string' ? css : objectToCssString(css)
  if (!cssText) return
  runtimeRules.set(selector, cssText)
  const el = getOrCreateRuntimeStyleElement()
  if (!el) return
  const lines = []
  for (const [sel, body] of runtimeRules) {
    lines.push(sel + '{' + body + '}')
  }
  el.textContent = lines.join('\n')
}

/** Shared: camelCase -> kebab-case for CSS property names */
export function camelToKebab(str) {
  return String(str).replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()
}

function objectToCssString(obj) {
  if (!obj || typeof obj !== 'object') return ''
  const parts = []
  for (const key of Object.keys(obj)) {
    const val = obj[key]
    if (val == null || val === '') continue
    const prop = camelToKebab(key)
    parts.push(prop + ':' + val)
  }
  return parts.join(';')
}

function mergeStyleArray(arr) {
  if (!Array.isArray(arr)) return arr
  const out = {}
  for (const item of arr) {
    if (item && typeof item === 'object') {
      Object.assign(out, item)
    }
  }
  return out
}

function escapeRuleBody(str) {
  return String(str)
    .replace(/\\/g, '\\\\')
    .replace(/}/g, '\\}')
}

export function styleToClass(style) {
  if (style === undefined || style === null) return ''
  if (typeof document === 'undefined') return ''

  let key
  if (typeof style === 'string') {
    key = style.trim()
  } else if (Array.isArray(style)) {
    const merged = mergeStyleArray(style)
    key = objectToCssString(merged)
  } else if (typeof style === 'object') {
    key = objectToCssString(style)
  } else {
    return ''
  }
  if (!key) return ''

  const cached = cache.get(key)
  if (cached) return cached

  const el = getOrCreateDynamicStyleElement()
  if (!el) return ''

  const className = CLASS_PREFIX + (++idCounter).toString(36)
  const safeBody = escapeRuleBody(key)
  const rule = '\n.' + className + '{' + safeBody + '}'
  el.textContent = (el.textContent || '') + rule
  cache.set(key, className)
  return className
}
